import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { validarArchivo } from "@/lib/adjuntos";
import { API, api, type Adjunto, type EscaneoDisponible, type EscaneoFactura, type Fideicomiso, type Gasto, type GastoDetalle, type ListadoGastos, type Proveedor, type Rubro, type TipoCambioSugerido } from "@/lib/api";
import {
  aPayloadGasto,
  feePrevisto,
  filtrosIniciales,
  formGasto,
  opcionesProveedor,
  queryGastos,
  refsDeGastos,
  rutaFicha,
  TODOS,
  validarGasto,
  type ErroresGasto,
  type FiltrosGastos,
  type FormGasto,
} from "@/lib/gastos";
import { opcionesFiltroRubro, opcionesRubro } from "@/lib/proveedores";
import { cambiarCampoFactura, conPresupuesto, formDesdeEscaneo } from "@/lib/facturas";
import { hoyISO } from "@/lib/format";
import { useCrearProveedor } from "./useCrearProveedor";
import { useVinculosFactura } from "./useVinculosFactura";

const SIN_ERRORES: ErroresGasto = {};
export const AVISO_SIN_ARCHIVO = "El gasto se guardó, pero no se pudo adjuntar el comprobante: subilo acá abajo.";
export const FALTA_COMPROBANTE = "Adjuntá el comprobante: la factura, el ticket o el recibo";

/** La factura escaneada: el archivo es el comprobante del alta; `escaneo` trae el nombre leído del proveedor. */
interface Escaneado {
  archivo: File;
  escaneo: EscaneoFactura;
}

/**
 * Lógica del listado de gastos (sin JSX). Admin carga; contador y fiduciante sólo miran. El fiduciante no lee la agenda
 * ni los rubros: sus filtros salen de los proveedores y rubros de todos los gastos (un pedido sin filtros).
 */
export function useGastosTab() {
  const { fid, membresia } = useContextoApp();
  const navegar = useNavigate();
  const base = `/api/fideicomisos/${fid}/gastos`;
  const esAdmin = membresia.rol === "admin";
  const esFiduciante = membresia.rol === "fiduciante";

  const [filtros, setFiltros] = useState<FiltrosGastos>(filtrosIniciales);
  const [params, setParams] = useSearchParams();
  const presupuesto = params.get("presupuesto");
  const listado = useApiResource<ListadoGastos>(`${base}${conPresupuesto(queryGastos(filtros), presupuesto)}`);
  const proveedores = useApiResource<Proveedor[]>(esFiduciante ? null : `/api/fideicomisos/${fid}/proveedores?archivados=true`);
  const rubros = useApiResource<Rubro[]>(esFiduciante ? null : `/api/fideicomisos/${fid}/rubros?archivados=true`);
  const todos = useApiResource<ListadoGastos>(esFiduciante ? base : null);
  const refs = refsDeGastos(todos.data?.items ?? []);
  const fideicomiso = useApiResource<Fideicomiso>(esAdmin ? `/api/fideicomisos/${fid}` : null);
  const tcSugerido = useApiResource<TipoCambioSugerido>(esAdmin ? `/api/fideicomisos/${fid}/tipo-cambio-sugerido` : null);

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<FormGasto>(() => formGasto());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionGuardar = useAsyncAction((datos: ReturnType<typeof aPayloadGasto>) => api.post<GastoDetalle>(base, datos));
  const escaneo = useApiResource<EscaneoDisponible>(esAdmin ? `${base}/escanear` : null);
  const [escaneado, setEscaneado] = useState<Escaneado | null>(null);
  const [errorArchivoEscaneo, setErrorArchivoEscaneo] = useState<string | null>(null);
  // Cada gasto lleva su comprobante (Mati, 2026-10-28): en el alta se elige el archivo, o es el escaneado.
  const [comprobante, setComprobante] = useState<File | null>(null);
  const [errorComprobante, setErrorComprobante] = useState<string | null>(null);
  const accionEscanear = useAsyncAction((archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api.upload<EscaneoFactura>(`${base}/escanear`, datos);
  });

  const errores = validarGasto(form);
  const feePct = fideicomiso.data?.fee_porcentaje ?? null;
  const rubrosSinFee = (rubros.data ?? []).filter((r) => r.clave !== "fee"); // el rubro del fee no se elige a mano

  const abrirNuevo = () => {
    setEscaneado(null);
    setComprobante(null);
    setErrorComprobante(null);
    setForm(formGasto(undefined, undefined, tcSugerido.data?.tipo_cambio));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    limpiarErrorProveedor();
    setAbierto(true);
  };

  const vinculos = useVinculosFactura(fid, form, esAdmin && abierto);
  const cambiar = <K extends keyof FormGasto>(campo: K, valor: FormGasto[K]) => setForm((f) => cambiarCampoFactura(f, campo, valor, vinculos.vinculables));
  const { crearProveedor, errorCrearProveedor, limpiarErrorProveedor } = useCrearProveedor(fid, proveedores.recargar, (id) => cambiar("proveedor_id", id));

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (!comprobante) setErrorComprobante(FALTA_COMPROBANTE);
    if (Object.keys(errores).length > 0 || !comprobante) return;
    const creado = await accionGuardar.run(aPayloadGasto(form));
    if (!creado) return;
    // El comprobante queda como adjunto del gasto; si la subida falla, la ficha lo avisa para subirlo a mano.
    const datos = new FormData();
    datos.append("archivo", comprobante);
    const aviso = await api.upload<Adjunto>(`${base}/${creado.id}/adjuntos`, datos).then(() => null, () => AVISO_SIN_ARCHIVO);
    setEscaneado(null);
    setComprobante(null);
    setAbierto(false);
    void navegar(`/gastos/${creado.id}`, aviso ? { state: { aviso } } : undefined);
  };

  const escanear = async (archivo: File) => {
    accionEscanear.limpiarError();
    const invalido = validarArchivo(archivo);
    setErrorArchivoEscaneo(invalido);
    if (invalido) return;
    const leido = await accionEscanear.run(archivo);
    if (!leido) return;
    setForm(formDesdeEscaneo(leido, hoyISO(), tcSugerido.data?.tipo_cambio));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    limpiarErrorProveedor();
    setEscaneado({ archivo, escaneo: leido });
    setComprobante(archivo);
    setErrorComprobante(null);
    setAbierto(true);
  };

  return {
    esAdmin,
    /** El fiduciante no ve presupuestos ni si falta el comprobante. */
    esFiduciante,
    /** El fee va sumado a su gasto, no como fila aparte (Mati, 2026-10-20 para el fiduciante y 2026-10-28 para todos). */
    items: (listado.data?.items ?? []).filter((g) => !g.es_fee),
    /** Si falla el pedido de un filtro, `listado.data` es el del filtro anterior: no se muestran sus totales. */
    totales: listado.error ? null : (listado.data?.totales ?? null),
    cargando: listado.loading && !listado.data,
    errorCarga: listado.error?.message ?? null,
    reintentar: listado.recargar,
    filtros,
    cambiarFiltro: <K extends keyof FiltrosGastos>(campo: K, valor: FiltrosGastos[K]) => setFiltros((f) => ({ ...f, [campo]: valor })),
    limpiarFiltros: () => setFiltros(filtrosIniciales()),
    hayFiltros: queryGastos(filtros) !== "",
    /** Desde la ficha de un presupuesto: sus facturas, con un Badge para quitar el filtro. */
    nombrePresupuestoFiltro: presupuesto ? (listado.data?.items.find((g) => String(g.cotizacion?.id) === presupuesto)?.cotizacion?.nombre ?? "el presupuesto") : null,
    quitarPresupuesto: () =>
      setParams((p) => {
        const nuevo = new URLSearchParams(p);
        nuevo.delete("presupuesto");
        return nuevo;
      }),
    opcionesProveedorFiltro: [{ valor: TODOS, etiqueta: "Todos los proveedores" }, ...(esFiduciante ? refs.proveedores : (proveedores.data ?? [])).map((p) => ({ valor: String(p.id), etiqueta: p.nombre }))],
    opcionesRubroFiltro: opcionesFiltroRubro(esFiduciante ? refs.rubros : (rubros.data ?? [])),
    abierto,
    cerrar: () => setAbierto(false),
    abrirNuevo,
    form,
    cambiar,
    errores: { ...(mostrarErrores ? errores : SIN_ERRORES), ...(errorCrearProveedor ? { proveedor_id: errorCrearProveedor } : {}) },
    crearProveedor,
    opcionesProveedorForm: opcionesProveedor(proveedores.data ?? []),
    opcionesRubroForm: opcionesRubro(rubrosSinFee),
    opcionesPresupuesto: vinculos.opcionesPresupuesto,
    opcionesCuota: vinculos.opcionesCuota,
    opcionesItem: vinculos.opcionesItem,
    feePrevisto: feePct && form.lleva_fee ? feePrevisto(form.monto, feePct) : null,
    feePorcentaje: feePct,
    comprobante,
    errorComprobante,
    elegirComprobante: (archivos: File[]) => {
      const archivo = archivos[0];
      if (!archivo) return;
      const invalido = validarArchivo(archivo);
      setErrorComprobante(invalido);
      setComprobante(invalido ? null : archivo);
    },
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    verFicha: (g: Gasto) => void navegar(rutaFicha(g)),
    /** El ícono de la columna Comprobante descarga el comprobante del gasto. */
    rutaComprobante: (adjuntoId: number) => `${API}/api/fideicomisos/${fid}/adjuntos/${adjuntoId}/descarga`,
    // escanear una factura (PDF o foto): abre el alta completada con lo que se leyó
    puedeEscanear: esAdmin && escaneo.data?.disponible === true,
    escanear: (archivos: File[]) => {
      if (archivos[0]) void escanear(archivos[0]);
    },
    escaneando: accionEscanear.loading,
    errorEscanear: errorArchivoEscaneo ?? accionEscanear.error,
    avisoEscaneo: abierto ? (escaneado?.escaneo.aviso ?? null) : null,
    /** El proveedor que figura en la factura escaneada y no está en la agenda (null si se encontró o ya se eligió otro). */
    proveedorLeidoNuevo: abierto && escaneado && !form.proveedor_id ? escaneado.escaneo.proveedor_nombre : null,
    agregarProveedorLeido: () => {
      if (escaneado?.escaneo.proveedor_nombre) crearProveedor(escaneado.escaneo.proveedor_nombre);
    },
  };
}
